import PropTypes from 'prop-types'
import React from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import { PermissionLevels } from 'utils/permission'
import { getData as apiGetData } from 'api/Api'
import { CancelError } from '@kanbo/api'
import {
  ControlLabel
} from 'react-bootstrap'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import classes from './KanBoPicker.module.scss'

const SelectMenu = components.Menu

const BOARD_LIMIT = 100

const emptyArray = []

export const styles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#fff',
    borderRadius: 5,
    borderColor: state.isFocused ? '#79c2ff' : '#e8e8e8',
    minHeight: null,
    boxShadow: null,
    '&:hover': null
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px',
    overflowY: 'hidden',
    overflowX: 'hidden'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 6
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10,
    borderRadius: 5,
    overflowY: 'auto'
  }),
  menuList: (base) => ({
    ...base,
    padding: 0
  }),
  option: (base, state) => ({
    ...base,
    zIndex: 11,
    position: 'relative',
    cursor: 'pointer',
    color: 'inherit',
    backgroundColor:
      state.isSelected || state.isFocused ? '#ecf6fe' : 'transparent',
    '&:active': {
      backgroundColor: '#ecf6fe'
    },
    '&:before': state.isSelected ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 5,
      backgroundColor: '#2196f3'
    } : null
  })
}

const defaultSelectProps = {
  styles: styles,
  menuPlacement: 'auto',
  menuPosition: 'absolute',
  menuPortalTarget: document.body,
}

const KanBoSelect = ({ intl, creatable, ...props }) =>
  creatable ? (
    <CreatableSelect
      {...defaultSelectProps}
      {...props}
      placeholder={intl.formatMessage({ id: 'KANBO_PICKER_SELECT' })}
      loadingMessage={() => intl.formatMessage({ id: 'LOADING' })}
      allowCreateWhileLoading
      isValidNewOption={(inputValue) => !!inputValue}
      formatCreateLabel={
        (inputValue) => intl.formatMessage(
          { id: 'KANBO_PICKER_SELECT_CREATE' },
          { cardName: inputValue }
        )
      }
    />
  ) : (
    <Select
      {...defaultSelectProps}
      {...props}
      placeholder={intl.formatMessage({ id: 'KANBO_PICKER_SELECT' })}
      loadingMessage={() => intl.formatMessage({ id: 'LOADING' })}
    />
  )

export class KanBoPicker extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,

    board: PropTypes.bool,
    status: PropTypes.bool,
    card: PropTypes.bool,
    currentBoardId: PropTypes.number,
    currentStatusId: PropTypes.number,
    boardTitle: PropTypes.string,
    statusTitle: PropTypes.string,
    cardTitle: PropTypes.string,
    requiredPermissionLevel: PropTypes.number,
    className: PropTypes.string,
    boardMulti: PropTypes.bool,
    cardFilter: PropTypes.func,
    onBoardChange: PropTypes.func,
    onStatusChange: PropTypes.func,
    onCardChange: PropTypes.func
  }

  static defaultProps = {
    requiredPermissionLevel: PermissionLevels.VISITOR
  }

  constructor (props) {
    super(props)

    this.state = {
      defaultBoardOption:
        props.currentBoardId
          ? {
            value: props.currentBoardId.toString(),
            label: '- ' + props.intl.formatMessage({ id: 'KANBO_PICKER_BOARDS_CURRENT_BOARD' }) + ' -'
          }
          : null,
      boardOption: null,
      statusOption: null,
      cardOption: null,
      boardInputValue: '',
      statusInputValue: '',
      cardInputValue: '',
      boardLoading: false,
      cardLoading: false,
      boardOptions: emptyArray,
      statusOptions: emptyArray,
      cardOptions: emptyArray
    }
  }

  componentDidMount = () => {
    const {
      board,
      status,
      card,
      onBoardChange
    } = this.props
    const {
      defaultBoardOption,
      boardOption,
      statusOption
    } = this.state

    if (board && defaultBoardOption) {
      onBoardChange && onBoardChange(
        +defaultBoardOption.value
      )
    }

    this.fetchBoards()

    if (status && boardOption) {
      this.fetchStatuses()
    }

    if (card && statusOption) {
      this.fetchCards()
    }
  }

  componentWillReceiveProps = (nextProps) => {
    if (
      nextProps.intl !== this.props.intl ||
      nextProps.currentBoardId !== this.props.currentBoardId
    ) {
      this.setState({
        defaultBoardOption: {
          value: nextProps.currentBoardId.toString(),
          label: '- ' + nextProps.intl.formatMessage({ id: 'KANBO_PICKER_BOARDS_CURRENT_BOARD' }) + ' -'
        }
      })
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const {
      status,
      card,
      boardMulti,
      onBoardChange,
      onStatusChange,
      onCardChange
    } = this.props
    const {
      boardOption,
      boardInputValue,
      statusOption,
      cardOption
    } = this.state

    if (boardInputValue !== prevState.boardInputValue) {
      this.fetchBoards()
    }

    if (boardOption !== prevState.boardOption) {
      this.setState({
        statusOption: null
      })

      if (onBoardChange) {
        if (boardMulti) {
          onBoardChange(
            boardOption
              ? boardOption.map(option => +option.value)
              : null
          )
        } else {
          onBoardChange(
            boardOption ? +boardOption.value : null
          )
        }
      }

      if (status && boardOption) {
        this.fetchStatuses()
      }
    }

    if (statusOption !== prevState.statusOption) {
      this.setState({
        cardOption: null
      })

      onStatusChange && onStatusChange(
        statusOption ? +statusOption.value : null
      )

      if (card && statusOption) {
        this.fetchCards()
      }
    }

    if (cardOption !== prevState.cardOption) {  
      onCardChange && onCardChange(
        !cardOption ? null : cardOption.__isNew__ ? null : +cardOption.value,
        !cardOption ? null : cardOption.__isNew__ ? cardOption.value : null
      )
    }
  }

  componentWillUnmount () {
    this.cancelFetchBoards()
    this.cancelFetchStatuses()
    this.cancelFetchCards()
  }

  fetchBoards = () => {
    const {
      boardMulti,
      requiredPermissionLevel
    } = this.props
    const {
      defaultBoardOption,
      boardOption,
      boardInputValue
    } = this.state

    this.cancelFetchBoards()

    this.setState({
      boardOptions: emptyArray,
      boardLoading: true
    })

    this.fetchBoardsPromise = apiGetData('Boards', {
      limit: BOARD_LIMIT,
      requiredPermissionLevel,
      query: boardInputValue
    })
    this.fetchBoardsPromise.then((data) => {
      const { Results } = data

      let boardOptions =
        Results
          .filter(Board => !Board.IsLocked)
          .map(Board => ({
            value: Board.Id.toString(),
            label: Board.Name
          }))

      if (!boardMulti && defaultBoardOption) {
        boardOptions = [
          defaultBoardOption,
          ...boardOptions
        ]
      }

      if (!boardMulti && !boardOption) {
        this.setState(({
          boardOption:
            defaultBoardOption ||
            (boardOptions.length > 0 && boardOptions[0]) ||
            null
        }))
      }

      this.setState(({
        boardOptions,
        boardLoading: false
      }))
    }).catch((error) => {
      if (!(error instanceof CancelError)) {
        throw error
      }
    })
  }

  cancelFetchBoards = () => {
    if (this.fetchBoardsPromise) {
      if (this.fetchBoardsPromise.cancel) {
        this.fetchBoardsPromise.cancel()
      }

      delete this.fetchBoardsPromise
    }
  }

  fetchStatuses = () => {
    const {
      currentStatusId
    } = this.props
    const {
      boardOption
    } = this.state

    this.cancelFetchStatuses()

    if (!boardOption) {
      this.setState({
        statusOptions: emptyArray
      })
      return
    }

    this.setState({
      statusOption: null,
      statusOptions: emptyArray,
      statusLoading: true
    })

    this.fetchStatusesPromise = apiGetData('Board', { id: +boardOption.value })
    this.fetchStatusesPromise.then((data) => {
      const { Statuses } = data

      const statusOptions =
        Statuses.map(Status => ({
          value: Status.Id.toString(),
          label: Status.Name
        }))

      let currentStatusOption = null
      if (currentStatusId) {
        currentStatusOption = statusOptions.find(option => option.value === currentStatusId.toString())
      }

      this.setState({
        statusOption:
          currentStatusOption ||
          (statusOptions.length > 0 && statusOptions[0]) ||
          null,
        statusOptions: statusOptions,
        statusLoading: false
      })
    }).catch((error) => {
      if (!(error instanceof CancelError)) {
        throw error
      }
    })
  }

  cancelFetchStatuses = () => {
    if (this.fetchStatusesPromise) {
      if (this.fetchStatusesPromise.cancel) {
        this.fetchStatusesPromise.cancel()
      }

      delete this.fetchStatusesPromise
    }
  }

  fetchCards = () => {
    const {
      cardFilter
    } = this.props
    const {
      boardOption,
      statusOption
    } = this.state

    this.cancelFetchCards()

    if (!statusOption) {
      this.setState({
        cardOptions: emptyArray
      })
      return
    }

    this.setState({
      cardOption: null,
      cardOptions: emptyArray,
      cardLoading: true
    })

    this.fetchCardsPromise = apiGetData('BoardCards', { boardId: +boardOption.value })
    this.fetchCardsPromise.then((data) => {
      const { Cards } = data

      let cardOptions =
        Cards
          .filter(Card =>
            Card.StatusId === +statusOption.value &&
            !Card.IsTemplate
          )
          .sort((a, b) => a.StatusPosition - b.StatusPosition)
          .map(Card => ({
            value: Card.Id.toString(),
            label: Card.Name,
            id: Card.Id
          }))

      if (cardFilter) {
        cardOptions = cardOptions.filter(cardFilter)
      }

      this.setState({
        cardOption: cardOptions.length > 0 ? cardOptions[0] : null,
        cardOptions,
        cardLoading: false
      })
    }).catch((error) => {
      if (!(error instanceof CancelError)) {
        throw error
      }
    })
  }

  cancelFetchCards = () => {
    if (this.fetchCardsPromise) {
      if (this.fetchCardsPromise.cancel) {
        this.fetchCardsPromise.cancel()
      }

      delete this.fetchCardsPromise
    }
  }

  handleBoardChange = (boardOption) => {
    const {
      boardMulti
    } = this.props

    if (boardMulti) {
      this.setState({
        boardOption
      })
    } else {
      // TODO: react-select returns empty array on empty search backspace
      this.setState({
        boardOption: Array.isArray(boardOption) ? null : boardOption
      })
    }
  }

  handleBoardInputChange = (boardInputValue) => {
    this.setState({
      boardInputValue
    })
  }

  handleStatusChange = (statusOption) => {
    this.setState({
      statusOption
    })
  }

  handleStatusInputChange = (statusInputValue) => {
    this.setState({
      statusInputValue
    })
  }

  handleCardChange = (cardOption) => {
    this.setState({
      cardOption
    })
  }

  handleCardInputChange = (cardInputValue) => {
    this.setState({
      cardInputValue
    })
  }

  render () {
    const {
      intl,

      board,
      status,
      card,
      boardTitle,
      statusTitle,
      cardTitle,
      className,
      boardMulti
    } = this.props
    const {
      boardOption,
      boardLoading,
      boardOptions,
      statusOption,
      statusInputValue,
      statusLoading,
      statusOptions,
      cardOption,
      cardInputValue,
      cardLoading,
      cardOptions
    } = this.state

    return (
      <div className={className}>
        {board &&
          <div className={classes.section}>
            <ControlLabel>
              {
                typeof boardTitle !== 'undefined'
                  ? boardTitle
                  : <FormattedMessage id={boardMulti ? 'KANBO_PICKER_BOARDS_TITLE' : 'KANBO_PICKER_BOARD_TITLE'} />
              }
            </ControlLabel>
            <KanBoSelect
              intl={intl}
              value={boardOption}
              options={boardOptions}
              isMulti={boardMulti}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? intl.formatMessage({ id: 'KANBO_PICKER_NO_RESULTS' })
                  : intl.formatMessage({ id: 'KANBO_PICKER_BOARDS_SEARCH' })
              }
              components={{
                Menu: (props) =>
                  <SelectMenu {...props}>
                    {!props.selectProps.inputValue &&
                      <div
                        style={{
                          padding: '5px 10px',
                          textAlign: 'center'
                        }}
                      >
                        <FormattedMessage id='KANBO_PICKER_BOARDS_SEARCH_HINT' />
                      </div>
                    }
                    {props.children}
                  </SelectMenu>
              }}
              isLoading={boardLoading}
              onChange={this.handleBoardChange}
              onInputChange={this.handleBoardInputChange}
            />
          </div>
        }
        {status &&
          <div className={classes.section}>
            <ControlLabel>
              {typeof statusTitle !== 'undefined' ? statusTitle : <FormattedMessage id='KANBO_PICKER_STATUS_TITLE' />}
            </ControlLabel>
            <KanBoSelect
              key={boardOption && boardOption.value}
              intl={intl}
              value={statusOption}
              options={statusOptions.filter(option => option.label.toLowerCase().indexOf(statusInputValue.toLowerCase()) !== -1)}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? intl.formatMessage({ id: 'KANBO_PICKER_NO_RESULTS' })
                  : null
              }
              isLoading={statusLoading}
              isDisabled={!boardOption}
              onChange={this.handleStatusChange}
              onInputChange={this.handleStatusInputChange}
            />
          </div>
        }
        {card &&
          <div className={classes.section}>
            <ControlLabel>
              {typeof cardTitle !== 'undefined' ? cardTitle : <FormattedMessage id='KANBO_PICKER_CARD_TITLE' />}
            </ControlLabel>
            <KanBoSelect
              key={statusOption && statusOption.value}
              intl={intl}
              creatable
              value={cardOption}
              options={cardOptions.filter(option => option.label.toLowerCase().indexOf(cardInputValue.toLowerCase()) !== -1)}
              isLoading={cardLoading}
              isDisabled={!statusOption}
              noOptionsMessage={() => intl.formatMessage({ id: 'KANBO_PICKER_NO_CARDS' })}
              onChange={this.handleCardChange}
              onInputChange={this.handleCardInputChange}
            />
          </div>
        }
      </div>
    )
  }
}

export default injectIntl(KanBoPicker)
